import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckSquare, Package, Clock, UserCheck, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { timeAgo } from "@/lib/helpers";

const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="border border-zinc-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="mt-2 font-heading text-3xl font-bold tracking-tight text-zinc-900">{value}</p>
      </div>
      <div className={`flex h-9 w-9 items-center justify-center ${accent ? "bg-[#b32233] text-white" : "bg-zinc-100 text-zinc-600"}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats)
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#b32233]" />
      </div>
    );

  const chartData = stats.tasks_by_status || [];

  return (
    <div className="p-6">
      <div className="mb-2">
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#b32233]">Overview</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
          Welcome back, {user?.name?.split(" ")[0]}
        </h2>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Tasks" value={stats.total_tasks} icon={CheckSquare} testid="stat-total-tasks" />
        <StatCard label="Assigned to me" value={stats.my_tasks} icon={UserCheck} accent testid="stat-my-tasks" />
        <StatCard label="Products" value={stats.total_products} icon={Package} testid="stat-total-products" />
        <StatCard label="Pending Approval" value={stats.pending_products} icon={Clock} testid="stat-pending-products" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border border-zinc-200 bg-white p-5 lg:col-span-2">
          <h3 className="mb-4 font-heading text-lg font-semibold tracking-tight">Tasks by status</h3>
          {chartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-zinc-400">No tasks yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717a" }} axisLine={{ stroke: "#e4e4e7" }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f4f4f5" }} contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="count" radius={0}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-zinc-200 bg-white p-5">
          <h3 className="mb-4 font-heading text-lg font-semibold tracking-tight">Recent activity</h3>
          <div className="space-y-3" data-testid="recent-activity">
            {(stats.recent_tasks || []).length === 0 && <p className="text-sm text-zinc-400">Nothing here yet</p>}
            {(stats.recent_tasks || []).map((t) => (
              <div key={t.id} className="border-l-2 border-zinc-200 pl-3">
                <p className="truncate text-sm font-medium text-zinc-900">{t.title}</p>
                <p className="text-xs text-zinc-400">
                  {t.assignee_name ? `@${t.assignee_name}` : "Unassigned"} · {timeAgo(t.updated_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
